import { Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import { useBreadcrumbs } from "components/Breadcrumbs/useWorkspaceBreadcrumbs";
import { useValueThroughContextMenu } from "components/ContextMenu/useValueThroughContextMenu";
import PageContent from "components/Layout/PageContent";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import RecordTable from "components/Table/RecordTable";
import TableTrashToggle from "components/Table/TableTrashToggle";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import { ExecutionTree } from "./ExecutionTree";
import { executionsListingColumns } from "config/executions";
import { CustomFilterValues } from "containers/AdvancedSearch/AdvancedSearch";
import { replaceSearchTermWithWildcards } from "containers/AdvancedSearch/utils";
import ExecutionFilter from "containers/ExecutionFilter";
import { getCustomFiltersFromUrl } from "containers/ExecutionFilter/CustomFilter/utils";
import { SearchField } from "containers/ExecutionFilter/interfaces";
import { useExecutionFilter } from "containers/ExecutionFilter/useExecutionFilter";
import { getLocationQuery, useQueryParams } from "containers/ExecutionFilter/utils";
import { ChildrenScopeToggle } from "containers/Executions/components/ChildrenScopeToggle";
import { ChildrenViewModeToggle } from "containers/Executions/components/ChildrenViewModeToggle";
import {
    getEdges,
    getNodes,
} from "containers/Resources/screens/ResourcesSingle/components/Visualization/DependencyVisualization/dependencyVisualization.selectors";
import { buildExecutionKindMap } from "containers/Resources/screens/ResourcesSingle/components/Visualization/DependencyVisualization/dependencyVisualization.service";
import { LiveUpdatesIcon } from "containers/Executions/components/LiveUpdatesIcon";
import { actions as executionActions } from "containers/Executions/Executions.reducer";
import { getExecutionsData } from "containers/Executions/Executions.selectors";
import { executionsListSubscription } from "containers/Executions/Executions.type";
import ActionToolbar from "containers/Executions/screens/ExecutionsListing/components/ActionToolbar";
import { ExecutionsListingPageHeader } from "containers/Executions/screens/ExecutionsListing/components/ExecutionsListingPageHeader/ExecutionsListingPageHeader";
import { HiddenResults } from "containers/Filter/components/HiddenResults";
import { useMatchingFields } from "containers/Filter/FilterBuilder/useMatchingFields";
import { FormApi } from "final-form";
import { Maybe } from "graphql/generated/graphql";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import { getBundlesById } from "containers/Bundles/Bundles.selectors";
import { getProjectsById } from "containers/Projects/Projects.selectors";
import { getWorkspaceData } from "containers/Workspaces/Workspaces.selectors";
import useReplaceIdWithName from "utils/useReplaceIdWithName";
import { useHistory } from "react-router-dom";
import ReconnectingWebSocket from "reconnecting-websocket";
import { ErrorBoundary } from "utils/RouterManager/screens/Error";
import { useMatchExecutionFilterLocation } from "utils/RouterManager/utils";
import { useVisibilityTabChange } from "utils/useVisibilityTabChange";
import { getSearchField, parseFilterSafely } from "utils/utils";
import "./ExecutionsListing.scss";

interface ExecutionsListingProps {
    parentExecutionId?: string | null;
    setFilterOnMount?: boolean;
}

const initialRowsPerPage = 10;
const initialQuery = {
    limit: initialRowsPerPage as typeof initialRowsPerPage,
    offset: 0,
    order: "-created_at",
};
const tableSettingsKey = "execution_filter";
const queryWithTableSettings = getInitialQueryWithTableSettings(tableSettingsKey, initialQuery);
// Tree view assembles the whole descendant set client-side, so it fetches an
// unpaginated batch in one subscription instead of a single 10-row page. The cap
// stays under the backend GraphQL query-cost gate (aiohttprest
// QueryComplexityLimitRule, cost_limit 2000): the executions-list subscription
// costs (row_field_cost + 1) * limit ≈ 13 * limit, so a limit of 150 keeps the
// cost (~1950) below the gate. A higher limit (e.g. 500 → cost 6500) makes the
// backend reject the subscription and the tree renders empty. Descendants beyond
// the cap surface via the tree's truncation note.
const treeFetchLimit = 150;

const hasDeleted = (values, { isStringType = false }: { isStringType?: boolean } = {}) =>
    values.include_deleted === (isStringType ? "true" : true);
const hasNormal = (values, { isStringType = false }: { isStringType?: boolean } = {}) =>
    values.hide_deleted !== (isStringType ? "false" : false);

const ExecutionsListing = ({ parentExecutionId, setFilterOnMount }: ExecutionsListingProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { queryParams } = useQueryParams();

    const { executionFilterLocation: resourceType, executionFilterLocationId: resourceID } =
        useMatchExecutionFilterLocation();

    const workspaceData = useSelector(getWorkspaceData);
    const projectsById = useSelector(getProjectsById);
    const bundlesById = useSelector(getBundlesById);
    const containerName =
        resourceType === "workspace"
            ? workspaceData?.name
            : resourceType === "project"
            ? projectsById[resourceID ?? ""]?.name
            : resourceType === "bundle"
            ? bundlesById[resourceID ?? ""]?.name
            : undefined;
    useReplaceIdWithName(resourceID, containerName);

    const [query, setQuery] = useState(queryWithTableSettings);

    const updateQuery = useCallback(
        (queryChange: any) => {
            setQuery({
                ...query,
                ...queryChange,
            });
        },
        [query]
    );

    const hasParentExecutionId = !!parentExecutionId;
    // On a container listing (workspace/project/bundle) the resourceID (and thus the filter
    // storage key) resolves asynchronously from Redux on cold load. Until it is known, withhold
    // the on-mount initial-filter URL write so it does not ping-pong with the strip effect.
    const isContainerContextPending = !hasParentExecutionId && !!resourceType && !resourceID;
    const {
        canSearchEverywhere,
        onChangeHandlers: { setInitialFilterHandler },
        filteredValues,
        setQueryString,
        initialFilterWithLocation,
        effectiveQueryParams,
        hasStoredFilter,
        isFilterInitialized,
    } = useExecutionFilter(
        queryParams,
        history,
        hasParentExecutionId,
        hasParentExecutionId && parentExecutionId
            ? { recordType: "execution", recordId: parentExecutionId }
            : resourceType && resourceID
            ? { recordType: resourceType, recordId: resourceID }
            : undefined,
        isContainerContextPending
    );

    const customFilterValues = useMemo<CustomFilterValues>(
        () => getCustomFiltersFromUrl(effectiveQueryParams),
        [effectiveQueryParams]
    );

    const [filtersPristine, setFiltersPristine] = useState(true);
    const [isLiveUpdatesEnabled, setLiveUpdatesEnabled] = useState(true);
    // Children tab: default to the full dependency-descendant set (consistent with the
    // dependency visualization); the toggle flips to direct-children-only.
    const [showAllDescendants, setShowAllDescendants] = useState(true);
    // Children tab: flat list (default) vs. an expandable parent/child tree.
    const [isTreeView, setIsTreeView] = useState(false);
    const isTreeMode = isTreeView && !!parentExecutionId;

    const baseFilter = useMemo(() => {
        const filterDisjunction: any = { or: [] };

        if (resourceType === "workspace") {
            filterDisjunction.or.push({
                field: "workspace_id",
                op: "eq",
                value: resourceID,
            });
        } else if (resourceType === "project" || resourceType === "bundle") {
            if (resourceID) {
                filterDisjunction.or.push({
                    field: `${resourceType}_id`,
                    op: "eq",
                    value: resourceID,
                });
            } else {
                filterDisjunction.or.push({
                    field: `${resourceType}_id`,
                    op: "unset",
                    value: null,
                });
            }
        }

        if (parentExecutionId) {
            filterDisjunction.or.push({
                // `descendant_of` = full recursive descendant subtree (matches the
                // dependency visualization); `parent_execution_id` = direct children only.
                field: showAllDescendants ? "descendant_of" : "parent_execution_id",
                op: "eq",
                value: parentExecutionId,
            });
        }

        return filterDisjunction;
    }, [parentExecutionId, resourceID, resourceType, showAllDescendants]);

    const { closeAllWebsocketConnections } = useVisibilityTabChange();

    const [websocketError, setWebsocketError] = useState(false);
    const [websocketConnectionsCount, setWebsocketConnectionsCount] = useState(0);
    const subscriptionEpochRef = useRef(0);

    useEffect(() => {
        const typedWindow: any = window;
        const websocketHost =
            typedWindow.REACT_APP_WEBSOCKET_API || `wss://${typedWindow.location.host}`;
        let socket: ReconnectingWebSocket | null = null;

        const shouldSubscribe =
            !closeAllWebsocketConnections && filtersPristine && isLiveUpdatesEnabled;

        const closeSocket = () => {
            if (socket) {
                socket.close();
                socket = null;
            }
        };

        const updateResourceFilter = () => {
            if (!shouldSubscribe) {
                return;
            }

            subscriptionEpochRef.current += 1;
            const epoch = subscriptionEpochRef.current;
            setWebsocketError(false);

            dispatch({
                type: executionActions.START_EXECUTION_FETCHING,
            });
            dispatch({
                type: executionActions.CLEAR_EXECUTIONS_DATA,
                response: null,
            });
            const filterConjunction = { and: [baseFilter] };

            if (effectiveQueryParams.filter) {
                const filterOptions = parseFilterSafely(effectiveQueryParams.filter);
                let searchField: Maybe<SearchField> = null;
                let locationField: any = null;

                filterOptions.and = (filterOptions.and || []).filter((field) => {
                    if (field.field === "name") {
                        searchField = getSearchField({ term: field.value });
                        return false;
                    }
                    if (field.field === "execution_type") {
                        field.field = "type";
                        field.value = field.value.map((typeValue) => typeValue.toUpperCase());
                    }
                    if (field.field === "location") {
                        locationField = getLocationQuery(field.value);
                        return false;
                    }

                    return field;
                });

                if (searchField) {
                    filterOptions.and.push(searchField);
                }
                if (locationField) {
                    filterOptions.and.push(locationField);
                }
                filterConjunction.and.push(filterOptions);
            }

            if (effectiveQueryParams.customFilter) {
                const parsedCustomFilter = parseFilterSafely(effectiveQueryParams.customFilter);
                const mappedFilterValues = replaceSearchTermWithWildcards(parsedCustomFilter);

                filterConjunction.and.push(mappedFilterValues);
            }

            const includeDeleted = hasDeleted(effectiveQueryParams, { isStringType: true });
            const includeNormal = hasNormal(effectiveQueryParams, { isStringType: true });

            const websocketParams = {
                subscription: executionsListSubscription,
                variables: {
                    order: query.order,
                    // Tree view needs the whole descendant set to assemble the
                    // hierarchy, so it fetches an unpaginated capped batch.
                    limit: isTreeMode ? treeFetchLimit : query.limit,
                    offset: isTreeMode ? 0 : query.offset,
                    // `$filter` is a GraphQL String — send it as a JSON string,
                    // the same shape POST /graphql queries use.
                    filter: JSON.stringify(filterConjunction),
                },
                allow_deleted: includeDeleted,
                allow_normal: includeNormal,
            };

            closeSocket();
            socket = new ReconnectingWebSocket(
                `${websocketHost}${typedWindow.REACT_APP_WEBSOCKET_API_NAMESPACE}/graphql?subscription_message=true`
            );
            const handleMessage = (event) => {
                if (epoch !== subscriptionEpochRef.current) {
                    return;
                }

                const response = JSON.parse(event.data);

                if (response.BadRequest) {
                    const { title, message } = response.BadRequest;
                    NotificationService.error(
                        <ExpandableNotification title={title} details={message} />
                    );
                    setWebsocketError(true);
                    setWebsocketConnectionsCount((count) => count + 1);
                    closeSocket();
                } else {
                    dispatch({
                        type: executionActions.STORE_EXECUTIONS_DATA,
                        response,
                    });
                }
            };
            socket.addEventListener("open", () => {
                socket?.send(JSON.stringify(websocketParams));
            });
            socket.addEventListener("message", handleMessage);
        };
        const unlisten = history.listen(() => {
            if (shouldSubscribe) {
                updateResourceFilter();
            }
        });

        if (shouldSubscribe) {
            updateResourceFilter();
        }

        return () => {
            closeSocket();
            unlisten();
        };
        // TODO (low) evaluate alternative fixes, exhaustive adding of deps seems unclean, esp. with additional noise of useWrapper etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        parentExecutionId,
        baseFilter,
        query,
        effectiveQueryParams,
        closeAllWebsocketConnections,
        filtersPristine,
        isLiveUpdatesEnabled,
        isTreeMode,
    ]);

    useEffect(() => {
        if (websocketConnectionsCount && websocketConnectionsCount % 10 === 0) {
            NotificationService.warn(
                "Websocket is trying to reconnect. Please wait or try to refresh the page."
            );
        }
    }, [websocketConnectionsCount]);

    const executionsSelector = useCallback((state) => getExecutionsData(state), []);
    const executions = useSelector(executionsSelector);
    const data = useMemo(
        () =>
            executions?.execution_list?.map((execution: any) => {
                return { ...execution, resource_type: "execution" };
            }) ?? [],
        [executions?.execution_list]
    );
    const totalRecordCount = executions?.execution_count;
    const isLoading = !websocketError && totalRecordCount == null;

    useEffect(() => {
        if (totalRecordCount > 0 && !data.length && query.offset) {
            updateQuery({ offset: 0 });
        }
    }, [data.length, query.offset, totalRecordCount, updateQuery]);

    const { matchingFields, filteredFieldList } = useMatchingFields({ activeTab: "execution" });

    const filterFormRef = useRef<FormApi | null>(null);

    const setFilterFormRef = useCallback((ref: FormApi | null) => {
        filterFormRef.current = ref;
    }, []);
    const { setValueThroughContextMenu } = useValueThroughContextMenu({
        customFormValues: customFilterValues,
        matchingFields,
        setCustomFormValues: (values) => {
            filterFormRef?.current?.mutators?.addCustomFilter(values);
        },
    });

    // Children-tab "Relation" column: reuse the dependency-visualization's already-loaded,
    // backend-tagged edges (shared redux slice) to show each descendant's relation kind
    // (child / dependency / both). Only meaningful when listing an execution's descendants,
    // so the column is omitted on the plain executions list (no parentExecutionId).
    const vizNodes = useSelector(getNodes);
    const vizEdges = useSelector(getEdges);
    const relationKindMap = useMemo(
        () => (parentExecutionId ? buildExecutionKindMap(vizNodes, vizEdges) : undefined),
        [parentExecutionId, vizNodes, vizEdges]
    );

    const columns = useMemo(
        () => executionsListingColumns(setValueThroughContextMenu, relationKindMap),
        [setValueThroughContextMenu, relationKindMap]
    );

    // One-click trash visibility toggle relocated into the RecordTable toolbar
    // (next to density/columns), consistent with the custom-objects list. It flips
    // only `include_deleted` (show/hide trashed) and always keeps non-trashed rows
    // shown (`hide_deleted: true`) — the finer "hide untrashed" granularity stays in
    // advanced search, per the intake. It applies immediately by driving the same
    // apply path as the filter form's apply button (form.change + form.submit),
    // so it stays a genuine one-click action without tripping the pristine gate.
    const onTrashToggle = useCallback((next: boolean) => {
        const form = filterFormRef.current;
        if (!form) {
            return;
        }
        form.batch(() => {
            form.change("include_deleted", next);
            form.change("hide_deleted", true);
        });
        form.submit();
    }, []);

    const trashToggle = useMemo(
        () => <TableTrashToggle showTrash={hasDeleted(filteredValues)} onToggle={onTrashToggle} />,
        [filteredValues, onTrashToggle]
    );

    const options = useMemo(
        () => ({
            currentOffset: query.offset,
            canSearchEverywhere,
            toolbar: "custom" as "custom",
            customLeftToolbar: ActionToolbar as (props: any) => any,
            customRightToolbarChildren: trashToggle,
        }),
        [canSearchEverywhere, query.offset, trashToggle]
    );

    const deletedOnly = useMemo(
        () => hasDeleted(filteredValues) && !hasNormal(filteredValues),
        [filteredValues]
    );
    const normalOnly = useMemo(
        () => !hasDeleted(filteredValues) && hasNormal(filteredValues),
        [filteredValues]
    );

    const { containerBreadcrumbs } = useBreadcrumbs();

    return (
        <>
            {!parentExecutionId && (
                <Breadcrumbs
                    breadcrumbs={[...containerBreadcrumbs, { link: "", label: "Executions" }]}
                />
            )}

            {parentExecutionId ? (
                <div className="executionsListingChildControls">
                    <LiveUpdatesIcon
                        isLiveUpdatesEnabled={isLiveUpdatesEnabled}
                        setLiveUpdatesEnabled={setLiveUpdatesEnabled}
                    />
                    <ChildrenScopeToggle
                        showAllDescendants={showAllDescendants}
                        setShowAllDescendants={setShowAllDescendants}
                    />
                    <ChildrenViewModeToggle
                        isTreeView={isTreeView}
                        setIsTreeView={setIsTreeView}
                    />
                </div>
            ) : (
                <ExecutionsListingPageHeader
                    isLiveUpdatesEnabled={isLiveUpdatesEnabled}
                    setLiveUpdatesEnabled={setLiveUpdatesEnabled}
                />
            )}
            <PageContent
                className={`page-content execution-search ${
                    isLiveUpdatesEnabled ? "" : "disabledSearch"
                }`}
            >
                <ExecutionFilter
                    hasParentExecutionId={hasParentExecutionId}
                    setFilterOnMount={setFilterOnMount}
                    filteredValues={filteredValues}
                    setQueryString={setQueryString}
                    setInitialFilterHandler={setInitialFilterHandler}
                    hasStoredFilter={hasStoredFilter}
                    isFilterInitialized={isFilterInitialized}
                    queryParams={effectiveQueryParams}
                    customFilterValues={customFilterValues}
                    setFilterFormRef={setFilterFormRef}
                    matchingFields={matchingFields}
                    filteredFieldList={filteredFieldList}
                    initialFilter={initialFilterWithLocation}
                    filtersPristine={filtersPristine}
                    setFiltersPristine={setFiltersPristine}
                />
            </PageContent>
            <PageContent className="executionsListing">
                {parentExecutionId && (
                    <div className="executionsListingListHeader">
                        <div className="executionsListingSpacer" />
                    </div>
                )}
                {filtersPristine ? (
                    isTreeMode ? (
                        <ExecutionTree
                            data={data}
                            columns={columns}
                            isLoading={isLoading}
                            totalItemCount={totalRecordCount}
                        />
                    ) : (
                        <RecordTable<any>
                            recordTypes="execution"
                            columns={columns}
                            data={data}
                            isLoading={isLoading}
                            totalItemCount={totalRecordCount}
                            updateQuery={updateQuery}
                            tableSettingsKey={tableSettingsKey}
                            initialQuery={queryWithTableSettings}
                            options={options}
                            className="stickyFooterTable"
                            isDisabled={!isLiveUpdatesEnabled}
                            filterParams={{
                                isTopLevel: filteredValues.no_parent_id,
                                deletedOnly,
                                normalOnly,
                            }}
                        />
                    )
                ) : (
                    <HiddenResults
                        onClick={() => {
                            filterFormRef.current?.submit();
                        }}
                        isDisabled={!isLiveUpdatesEnabled}
                    />
                )}
            </PageContent>
        </>
    );
};

ExecutionsListing.defaultProps = {
    parentExecutionId: null,
};

ExecutionsListing.propTypes = {
    parentExecutionId: PropTypes.string,
};

const WrappedExecutionsListing = withErrorBoundary(ExecutionsListing, {
    FallbackComponent: ErrorBoundary,
    onError(error) {
        console.error(error);
    },
});

export default WrappedExecutionsListing;
